/**
 * usePwaInstall — התקנת האפליקציה למסך הבית
 *
 * Everything the UI needs to offer "add to home screen":
 *   • isStandalone   — already running as an installed app (hide all prompts)
 *   • platform       — which set of instructions to show by default
 *   • isInAppBrowser — opened inside WhatsApp/Facebook/Instagram, where adding
 *                      to the home screen is impossible. This is the single
 *                      most common reason the guide "doesn't work": the link
 *                      was sent in WhatsApp and never left it.
 *   • canPrompt/promptInstall — Chrome/Edge on Android can install in one tap
 *                      via the captured beforeinstallprompt event; no guide
 *                      needed. iOS has no such API and never will, hence the
 *                      manual steps.
 */

import { useCallback, useEffect, useState } from 'react'

export type InstallPlatform = 'ios' | 'android' | 'desktop'

/** Not in lib.dom yet — Chromium-only event. */
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

function detectPlatform(): InstallPlatform {
  if (typeof navigator === 'undefined') return 'desktop'
  const ua = navigator.userAgent

  // iPadOS 13+ reports itself as a Mac; the touch-point count gives it away.
  const isIpadOS = /Macintosh/i.test(ua) && navigator.maxTouchPoints > 1
  if (/iPhone|iPad|iPod/i.test(ua) || isIpadOS) return 'ios'
  if (/Android/i.test(ua)) return 'android'
  return 'desktop'
}

/** Embedded webviews (WhatsApp, Messenger, Instagram, Facebook, Telegram, LinkedIn). */
function detectInAppBrowser(): boolean {
  if (typeof navigator === 'undefined') return false
  const ua = navigator.userAgent
  if (/FBAN|FBAV|FB_IAB|Instagram|Line\/|Twitter|LinkedInApp|MicroMessenger/i.test(ua)) return true
  // WhatsApp's Android webview identifies as wv; iOS WhatsApp appends "WhatsApp".
  if (/WhatsApp/i.test(ua)) return true
  if (/Android/i.test(ua) && /; wv\)/i.test(ua)) return true
  return false
}

/**
 * On iOS only Safari (and, since iOS 16.4, other browsers using their own
 * "Add to Home Screen") can install. We only need to warn about webviews,
 * which is handled above — but Chrome on iOS puts the item in a different
 * menu, so the guide mentions it.
 */
function detectIosBrowser(): 'safari' | 'chrome' | 'other' {
  if (typeof navigator === 'undefined') return 'safari'
  const ua = navigator.userAgent
  if (/CriOS/i.test(ua)) return 'chrome'
  if (/FxiOS|EdgiOS|OPiOS/i.test(ua)) return 'other'
  return 'safari'
}

function detectStandalone(): boolean {
  if (typeof window === 'undefined') return false
  const displayMode = window.matchMedia?.('(display-mode: standalone)')?.matches
  // iOS Safari exposes a non-standard flag instead of the media query.
  const iosStandalone = (window.navigator as Navigator & { standalone?: boolean }).standalone
  return Boolean(displayMode || iosStandalone)
}

export function usePwaInstall() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null)
  const [isStandalone, setIsStandalone] = useState(detectStandalone)
  const [wasInstalled, setWasInstalled] = useState(false)

  const [platform] = useState<InstallPlatform>(detectPlatform)
  const [isInAppBrowser] = useState<boolean>(detectInAppBrowser)
  const [iosBrowser] = useState(detectIosBrowser)

  useEffect(() => {
    const onBeforeInstallPrompt = (event: Event) => {
      // Keep the browser's own mini-infobar from firing so we can offer the
      // install at a moment that makes sense (our button / our banner).
      event.preventDefault()
      setDeferredPrompt(event as BeforeInstallPromptEvent)
    }
    const onInstalled = () => {
      setDeferredPrompt(null)
      setWasInstalled(true)
    }

    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt)
    window.addEventListener('appinstalled', onInstalled)

    // The user may install while the tab stays open.
    const media = window.matchMedia?.('(display-mode: standalone)')
    const onDisplayModeChange = () => setIsStandalone(detectStandalone())
    media?.addEventListener?.('change', onDisplayModeChange)

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt)
      window.removeEventListener('appinstalled', onInstalled)
      media?.removeEventListener?.('change', onDisplayModeChange)
    }
  }, [])

  /** Fires the native install dialog. Returns true if the user accepted. */
  const promptInstall = useCallback(async () => {
    if (!deferredPrompt) return false
    await deferredPrompt.prompt()
    const { outcome } = await deferredPrompt.userChoice
    // The event can only be used once; Chrome re-fires it if the user declines.
    setDeferredPrompt(null)
    return outcome === 'accepted'
  }, [deferredPrompt])

  return {
    /** Running inside the installed app — never nag here. */
    isStandalone,
    /** Installed during this visit (show a "done" confirmation). */
    wasInstalled,
    /** One-tap native install is available (Android Chrome/Edge, desktop Chrome). */
    canPrompt: deferredPrompt !== null,
    promptInstall,
    platform,
    iosBrowser,
    isInAppBrowser,
  }
}

export default usePwaInstall
